<!-- @catalog Expandable group of rows in a CGridTable: the parent row's cells go in #row, its sub-rows in the default slot. They slide open and shut with `expanded` (v-model) and are inert while shut. The chevron takes the table's first column, so give CGridTable a leading `auto` column and mark the sub-rows with CGridTableRow's `sub`. -->
<template>
  <c-grid-table-row>
    <div role="cell">
      <c-button
        v-if="expandable"
        color="transparent"
        text-color="navy"
        :icon="toClassicIcon(expanded ? 'fa-chevron-down' : 'fa-chevron-right')"
        :aria-expanded="expanded"
        :aria-label="`${label} ${expanded ? 'inklappen' : 'uitklappen'}`"
        @click="emit('update:expanded', expanded === false)"
      />
    </div>

    <slot name="row" />
  </c-grid-table-row>

  <div
    class="c-grid-table-group__panel"
    :class="{ 'c-grid-table-group__panel--open': expanded }"
    :inert="expanded === false ? true : undefined"
    role="rowgroup"
  >
    <div class="c-grid-table-group__rows" role="none">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { toClassicIcon } from "common/defaultIcons";

withDefaults(
  defineProps<{
    expanded: boolean;
    /** Names the group in the chevron's aria-label. */
    label: string;
    /** Without it the chevron stays out and the group never opens. */
    expandable?: boolean;
  }>(),
  { expandable: true },
);

const emit = defineEmits<{
  (event: "update:expanded", expanded: boolean): void;
}>();
</script>

<style lang="scss" scoped>
// Height animation without a fixed height: the panel is a one-row grid whose
// row track goes from 0fr to 1fr, and the inner element may shrink to that
// track (min-height: 0) while hiding what does not fit. Both keep the table's
// columns through subgrid, so the sub-rows still line up with the rest.
.c-grid-table-group {
  &__panel {
    @apply tw-grid !tw-border-t-0 tw-transition-[grid-template-rows] tw-duration-200 tw-ease-out;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    grid-template-rows: 0fr;

    &--open {
      grid-template-rows: 1fr;
    }
  }

  &__rows {
    @apply tw-grid tw-min-h-0 tw-overflow-hidden;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;

    // The table's dividers stop at the panel; the sub-rows draw their own.
    > * {
      @apply tw-border-t tw-border-gray-100;
    }
  }
}
</style>
